// src/i18n/translations.js
// Localization for all supported languages

export const LANGUAGES = {
  en: { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  pt: { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  fr: { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  zh: { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  de: { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  ja: { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
};

export const translations = {
  en: {
    // App
    appName: 'GovSky',
    
    // Onboarding
    onboarding: {
      welcome: 'Welcome to GovSky',
      subtitle: 'Your gateway to government news on Bluesky',
      selectCountry: 'Select your country',
      selectLanguage: 'Choose your language',
      continueButton: 'Continue',
      ageConfirm: 'By continuing, I confirm I am 16 years or older.',
      privacyLink: 'Privacy Policy',
      poweredBy: 'by',
    },
    
    // Header
    header: {
      login: 'Log in',
      logout: 'Log out',
      profile: 'Profile',
    },
    
    // Tabs
    tabs: {
      recent: 'Recent',
      trending: 'Trending',
      officials: 'Officials',
      saved: 'Saved',
    },
    
    // Feed
    feed: {
      loading: 'Loading...',
      loadMore: 'Load more',
      noPostsYet: 'No posts yet',
      noSavedPosts: 'No saved posts yet',
      savePostsHint: 'Bookmark posts to see them here',
      errorLoading: 'Error loading posts',
      pullToRefresh: 'Pull to refresh',
    },
    
    // Post
    post: {
      like: 'Like',
      liked: 'Liked',
      repost: 'Repost',
      reposted: 'Reposted',
      reply: 'Reply',
      share: 'Share',
      bookmark: 'Bookmark',
      bookmarked: 'Bookmarked',
      viewPost: 'View post',
      copiedToClipboard: 'Copied to clipboard',
      showMore: 'Show more',
      showLess: 'Show less',
      repostedBy: 'Reposted by',
      quotedPost: 'Quoted post',
    },
    
    // Login
    login: {
      title: 'Sign in to Bluesky',
      description: 'Use your Bluesky handle and app password to log in',
      handlePlaceholder: 'Handle (e.g. user.bsky.social)',
      passwordPlaceholder: 'App password',
      rememberMe: 'Remember me',
      loginButton: 'Log in',
      loggingIn: 'Logging in...',
      createAppPassword: 'Create an app password',
      appPasswordHint: 'For security, use an app password instead of your main password',
    },
    
    // Create Post
    createPost: {
      title: 'Create Post',
      placeholder: 'Share government news or insights...',
      findSources: 'Find government news on GovGlance.org',
      sources: 'Sources',
      sourcesHint: 'or govglance.org',
      addSource: 'Add source',
      sourcePlaceholder: 'https://...',
      post: 'Post',
      posting: 'Posting...',
      errorNoSource: 'Please include at least one source',
      errorInvalidSource: 'Please provide valid sources',
    },
    
    // Profile
    profile: {
      followers: 'Followers',
      following: 'Following',
      posts: 'Posts',
      follow: 'Follow',
      unfollow: 'Unfollow',
      editProfile: 'Edit profile',
      myPosts: 'My Posts',
    },
    
    // Country Picker
    countryPicker: {
      selectCountry: 'Select Country',
      language: 'Language',
      changeLanguage: 'Change language',
    },
    
    // Censored Content
    censoredContent: {
      warning: 'Content may contain sensitive language',
      show: 'Show',
      hide: 'Hide',
    },
    
    // Errors
    errors: {
      generic: 'Something went wrong',
      networkError: 'Network error. Please check your connection.',
      loginFailed: 'Login failed',
      postFailed: 'Failed to create post',
      loadFailed: 'Failed to load content',
    },
    
    // Common
    common: {
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      done: 'Done',
      retry: 'Retry',
      loading: 'Loading...',
    },
  },

  pt: {
    appName: 'GovSky',
    onboarding: {
      welcome: 'Bem-vindo ao GovSky',
      subtitle: 'Sua porta de entrada para notícias governamentais no Bluesky',
      selectCountry: 'Selecione seu país',
      selectLanguage: 'Escolha seu idioma',
      continueButton: 'Continuar',
      ageConfirm: 'Ao continuar, confirmo que tenho 16 anos ou mais.',
      privacyLink: 'Política de Privacidade',
      poweredBy: 'por',
    },
    header: {
      login: 'Entrar',
      logout: 'Sair',
      profile: 'Perfil',
    },
    tabs: {
      recent: 'Recentes',
      trending: 'Em alta',
      officials: 'Oficiais',
      saved: 'Salvos',
    },
    feed: {
      loading: 'Carregando...',
      loadMore: 'Carregar mais',
      noPostsYet: 'Nenhuma publicação ainda',
      noSavedPosts: 'Nenhuma publicação salva',
      savePostsHint: 'Salve publicações para vê-las aqui',
      errorLoading: 'Erro ao carregar publicações',
      pullToRefresh: 'Puxe para atualizar',
    },
    post: {
      like: 'Curtir',
      liked: 'Curtido',
      repost: 'Repostar',
      reposted: 'Repostado',
      reply: 'Responder',
      share: 'Compartilhar',
      bookmark: 'Salvar',
      bookmarked: 'Salvo',
      viewPost: 'Ver publicação',
      copiedToClipboard: 'Copiado para a área de transferência',
      showMore: 'Mostrar mais',
      showLess: 'Mostrar menos',
      repostedBy: 'Repostado por',
      quotedPost: 'Publicação citada',
    },
    login: {
      title: 'Entrar no Bluesky',
      description: 'Use seu handle do Bluesky e senha de aplicativo para entrar',
      handlePlaceholder: 'Handle (ex: usuario.bsky.social)',
      passwordPlaceholder: 'Senha do aplicativo',
      rememberMe: 'Lembrar de mim',
      loginButton: 'Entrar',
      loggingIn: 'Entrando...',
      createAppPassword: 'Criar senha de aplicativo',
      appPasswordHint: 'Por segurança, use uma senha de aplicativo em vez da senha principal',
    },
    createPost: {
      title: 'Criar Publicação',
      placeholder: 'Compartilhe notícias ou insights governamentais...',
      findSources: 'Encontre notícias governamentais no GovGlance.org',
      sources: 'Fontes',
      sourcesHint: 'ou govglance.org',
      addSource: 'Adicionar fonte',
      sourcePlaceholder: 'https://...',
      post: 'Publicar',
      posting: 'Publicando...',
      errorNoSource: 'Inclua pelo menos uma fonte',
      errorInvalidSource: 'Forneça fontes válidas',
    },
    profile: {
      followers: 'Seguidores',
      following: 'Seguindo',
      posts: 'Publicações',
      follow: 'Seguir',
      unfollow: 'Deixar de seguir',
      editProfile: 'Editar perfil',
      myPosts: 'Minhas Publicações',
    },
    countryPicker: {
      selectCountry: 'Selecionar País',
      language: 'Idioma',
      changeLanguage: 'Alterar idioma',
    },
    censoredContent: {
      warning: 'Conteúdo pode conter linguagem sensível',
      show: 'Mostrar',
      hide: 'Ocultar',
    },
    errors: {
      generic: 'Algo deu errado',
      networkError: 'Erro de rede. Verifique sua conexão.',
      loginFailed: 'Falha no login',
      postFailed: 'Falha ao criar publicação',
      loadFailed: 'Falha ao carregar conteúdo',
    },
    common: {
      cancel: 'Cancelar',
      save: 'Salvar',
      delete: 'Excluir',
      edit: 'Editar',
      close: 'Fechar',
      back: 'Voltar',
      next: 'Próximo',
      done: 'Concluído',
      retry: 'Tentar novamente',
      loading: 'Carregando...',
    },
  },

  fr: {
    appName: 'GovSky',
    onboarding: {
      welcome: 'Bienvenue sur GovSky',
      subtitle: 'Votre portail vers les actualités gouvernementales sur Bluesky',
      selectCountry: 'Sélectionnez votre pays',
      selectLanguage: 'Choisissez votre langue',
      continueButton: 'Continuer',
      ageConfirm: 'En continuant, je confirme avoir 16 ans ou plus.',
      privacyLink: 'Politique de confidentialité',
      poweredBy: 'par',
    },
    header: {
      login: 'Connexion',
      logout: 'Déconnexion',
      profile: 'Profil',
    },
    tabs: {
      recent: 'Récents',
      trending: 'Tendances',
      officials: 'Officiels',
      saved: 'Enregistrés',
    },
    feed: {
      loading: 'Chargement...',
      loadMore: 'Charger plus',
      noPostsYet: 'Aucune publication pour le moment',
      noSavedPosts: 'Aucune publication enregistrée',
      savePostsHint: 'Enregistrez des publications pour les voir ici',
      errorLoading: 'Erreur lors du chargement',
      pullToRefresh: 'Tirez pour actualiser',
    },
    post: {
      like: 'Aimer',
      liked: 'Aimé',
      repost: 'Republier',
      reposted: 'Republié',
      reply: 'Répondre',
      share: 'Partager',
      bookmark: 'Enregistrer',
      bookmarked: 'Enregistré',
      viewPost: 'Voir la publication',
      copiedToClipboard: 'Copié dans le presse-papiers',
      showMore: 'Afficher plus',
      showLess: 'Afficher moins',
      repostedBy: 'Republié par',
      quotedPost: 'Publication citée',
    },
    login: {
      title: 'Connexion à Bluesky',
      description: 'Utilisez votre identifiant Bluesky et mot de passe d\'application',
      handlePlaceholder: 'Identifiant (ex: user.bsky.social)',
      passwordPlaceholder: 'Mot de passe d\'application',
      rememberMe: 'Se souvenir de moi',
      loginButton: 'Connexion',
      loggingIn: 'Connexion en cours...',
      createAppPassword: 'Créer un mot de passe d\'application',
      appPasswordHint: 'Par sécurité, utilisez un mot de passe d\'application',
    },
    createPost: {
      title: 'Créer une publication',
      placeholder: 'Partagez des actualités gouvernementales...',
      findSources: 'Trouvez des actualités sur GovGlance.org',
      sources: 'Sources',
      sourcesHint: 'ou govglance.org',
      addSource: 'Ajouter une source',
      sourcePlaceholder: 'https://...',
      post: 'Publier',
      posting: 'Publication...',
      errorNoSource: 'Veuillez inclure au moins une source',
      errorInvalidSource: 'Veuillez fournir des sources valides',
    },
    profile: {
      followers: 'Abonnés',
      following: 'Abonnements',
      posts: 'Publications',
      follow: 'Suivre',
      unfollow: 'Ne plus suivre',
      editProfile: 'Modifier le profil',
      myPosts: 'Mes Publications',
    },
    countryPicker: {
      selectCountry: 'Sélectionner le pays',
      language: 'Langue',
      changeLanguage: 'Changer de langue',
    },
    censoredContent: {
      warning: 'Contenu potentiellement sensible',
      show: 'Afficher',
      hide: 'Masquer',
    },
    errors: {
      generic: 'Une erreur s\'est produite',
      networkError: 'Erreur réseau. Vérifiez votre connexion.',
      loginFailed: 'Échec de la connexion',
      postFailed: 'Échec de la publication',
      loadFailed: 'Échec du chargement',
    },
    common: {
      cancel: 'Annuler',
      save: 'Enregistrer',
      delete: 'Supprimer',
      edit: 'Modifier',
      close: 'Fermer',
      back: 'Retour',
      next: 'Suivant',
      done: 'Terminé',
      retry: 'Réessayer',
      loading: 'Chargement...',
    },
  },

  zh: {
    appName: 'GovSky',
    onboarding: {
      welcome: '欢迎使用 GovSky',
      subtitle: '您在 Bluesky 上获取政府新闻的门户',
      selectCountry: '选择您的国家',
      selectLanguage: '选择您的语言',
      continueButton: '继续',
      ageConfirm: '继续即表示我确认已年满16岁。',
      privacyLink: '隐私政策',
      poweredBy: '来自',
    },
    header: {
      login: '登录',
      logout: '退出',
      profile: '个人资料',
    },
    tabs: {
      recent: '最新',
      trending: '热门',
      officials: '官方',
      saved: '已保存',
    },
    feed: {
      loading: '加载中...',
      loadMore: '加载更多',
      noPostsYet: '暂无帖子',
      noSavedPosts: '暂无保存的帖子',
      savePostsHint: '收藏帖子后会显示在这里',
      errorLoading: '加载帖子时出错',
      pullToRefresh: '下拉刷新',
    },
    post: {
      like: '喜欢',
      liked: '已喜欢',
      repost: '转发',
      reposted: '已转发',
      reply: '回复',
      share: '分享',
      bookmark: '收藏',
      bookmarked: '已收藏',
      viewPost: '查看帖子',
      copiedToClipboard: '已复制到剪贴板',
      showMore: '显示更多',
      showLess: '收起',
      repostedBy: '转发自',
      quotedPost: '引用帖子',
    },
    login: {
      title: '登录 Bluesky',
      description: '使用您的 Bluesky 账号和应用密码登录',
      handlePlaceholder: '账号 (例如: user.bsky.social)',
      passwordPlaceholder: '应用密码',
      rememberMe: '记住我',
      loginButton: '登录',
      loggingIn: '登录中...',
      createAppPassword: '创建应用密码',
      appPasswordHint: '为安全起见，请使用应用密码而非主密码',
    },
    createPost: {
      title: '发布帖子',
      placeholder: '分享政府新闻或见解...',
      findSources: '在 GovGlance.org 查找政府新闻',
      sources: '来源',
      sourcesHint: '或 govglance.org',
      addSource: '添加来源',
      sourcePlaceholder: 'https://...',
      post: '发布',
      posting: '发布中...',
      errorNoSource: '请至少包含一个来源',
      errorInvalidSource: '请提供有效的来源',
    },
    profile: {
      followers: '粉丝',
      following: '关注',
      posts: '帖子',
      follow: '关注',
      unfollow: '取消关注',
      editProfile: '编辑资料',
      myPosts: '我的帖子',
    },
    countryPicker: {
      selectCountry: '选择国家',
      language: '语言',
      changeLanguage: '更改语言',
    },
    censoredContent: {
      warning: '内容可能包含敏感语言',
      show: '显示',
      hide: '隐藏',
    },
    errors: {
      generic: '出了点问题',
      networkError: '网络错误，请检查您的连接',
      loginFailed: '登录失败',
      postFailed: '发布失败',
      loadFailed: '加载失败',
    },
    common: {
      cancel: '取消',
      save: '保存',
      delete: '删除',
      edit: '编辑',
      close: '关闭',
      back: '返回',
      next: '下一步',
      done: '完成',
      retry: '重试',
      loading: '加载中...',
    },
  },

  de: {
    appName: 'GovSky',
    onboarding: {
      welcome: 'Willkommen bei GovSky',
      subtitle: 'Ihr Portal zu Regierungsnachrichten auf Bluesky',
      selectCountry: 'Wählen Sie Ihr Land',
      selectLanguage: 'Wählen Sie Ihre Sprache',
      continueButton: 'Weiter',
      ageConfirm: 'Mit dem Fortfahren bestätige ich, dass ich 16 Jahre oder älter bin.',
      privacyLink: 'Datenschutzrichtlinie',
      poweredBy: 'von',
    },
    header: {
      login: 'Anmelden',
      logout: 'Abmelden',
      profile: 'Profil',
    },
    tabs: {
      recent: 'Aktuell',
      trending: 'Trending',
      officials: 'Offizielle',
      saved: 'Gespeichert',
    },
    feed: {
      loading: 'Laden...',
      loadMore: 'Mehr laden',
      noPostsYet: 'Noch keine Beiträge',
      noSavedPosts: 'Keine gespeicherten Beiträge',
      savePostsHint: 'Speichern Sie Beiträge, um sie hier zu sehen',
      errorLoading: 'Fehler beim Laden der Beiträge',
      pullToRefresh: 'Zum Aktualisieren ziehen',
    },
    post: {
      like: 'Gefällt mir',
      liked: 'Gefällt mir',
      repost: 'Teilen',
      reposted: 'Geteilt',
      reply: 'Antworten',
      share: 'Teilen',
      bookmark: 'Speichern',
      bookmarked: 'Gespeichert',
      viewPost: 'Beitrag ansehen',
      copiedToClipboard: 'In Zwischenablage kopiert',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen',
      repostedBy: 'Geteilt von',
      quotedPost: 'Zitierter Beitrag',
    },
    login: {
      title: 'Bei Bluesky anmelden',
      description: 'Verwenden Sie Ihren Bluesky-Handle und App-Passwort',
      handlePlaceholder: 'Handle (z.B. user.bsky.social)',
      passwordPlaceholder: 'App-Passwort',
      rememberMe: 'Angemeldet bleiben',
      loginButton: 'Anmelden',
      loggingIn: 'Anmeldung...',
      createAppPassword: 'App-Passwort erstellen',
      appPasswordHint: 'Verwenden Sie aus Sicherheitsgründen ein App-Passwort',
    },
    createPost: {
      title: 'Beitrag erstellen',
      placeholder: 'Teilen Sie Regierungsnachrichten...',
      findSources: 'Finden Sie Nachrichten auf GovGlance.org',
      sources: 'Quellen',
      sourcesHint: 'oder govglance.org',
      addSource: 'Quelle hinzufügen',
      sourcePlaceholder: 'https://...',
      post: 'Posten',
      posting: 'Wird gepostet...',
      errorNoSource: 'Bitte mindestens eine Quelle angeben',
      errorInvalidSource: 'Bitte gültige Quellen angeben',
    },
    profile: {
      followers: 'Follower',
      following: 'Folgt',
      posts: 'Beiträge',
      follow: 'Folgen',
      unfollow: 'Entfolgen',
      editProfile: 'Profil bearbeiten',
      myPosts: 'Meine Beiträge',
    },
    countryPicker: {
      selectCountry: 'Land auswählen',
      language: 'Sprache',
      changeLanguage: 'Sprache ändern',
    },
    censoredContent: {
      warning: 'Inhalt kann sensible Sprache enthalten',
      show: 'Anzeigen',
      hide: 'Ausblenden',
    },
    errors: {
      generic: 'Etwas ist schiefgelaufen',
      networkError: 'Netzwerkfehler. Überprüfen Sie Ihre Verbindung.',
      loginFailed: 'Anmeldung fehlgeschlagen',
      postFailed: 'Beitrag konnte nicht erstellt werden',
      loadFailed: 'Inhalt konnte nicht geladen werden',
    },
    common: {
      cancel: 'Abbrechen',
      save: 'Speichern',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      close: 'Schließen',
      back: 'Zurück',
      next: 'Weiter',
      done: 'Fertig',
      retry: 'Erneut versuchen',
      loading: 'Laden...',
    },
  },

  ja: {
    appName: 'GovSky',
    onboarding: {
      welcome: 'GovSkyへようこそ',
      subtitle: 'Blueskyで政府ニュースにアクセス',
      selectCountry: '国を選択',
      selectLanguage: '言語を選択',
      continueButton: '続ける',
      ageConfirm: '続行することで、16歳以上であることを確認します。',
      privacyLink: 'プライバシーポリシー',
      poweredBy: 'から',
    },
    header: {
      login: 'ログイン',
      logout: 'ログアウト',
      profile: 'プロフィール',
    },
    tabs: {
      recent: '最新',
      trending: 'トレンド',
      officials: '公式',
      saved: '保存済み',
    },
    feed: {
      loading: '読み込み中...',
      loadMore: 'もっと読み込む',
      noPostsYet: '投稿はまだありません',
      noSavedPosts: '保存した投稿はありません',
      savePostsHint: '投稿をブックマークするとここに表示されます',
      errorLoading: '投稿の読み込みエラー',
      pullToRefresh: '引いて更新',
    },
    post: {
      like: 'いいね',
      liked: 'いいね済み',
      repost: 'リポスト',
      reposted: 'リポスト済み',
      reply: '返信',
      share: '共有',
      bookmark: 'ブックマーク',
      bookmarked: 'ブックマーク済み',
      viewPost: '投稿を見る',
      copiedToClipboard: 'クリップボードにコピーしました',
      showMore: '続きを見る',
      showLess: '閉じる',
      repostedBy: 'リポスト元',
      quotedPost: '引用投稿',
    },
    login: {
      title: 'Blueskyにログイン',
      description: 'Blueskyのハンドルとアプリパスワードでログイン',
      handlePlaceholder: 'ハンドル (例: user.bsky.social)',
      passwordPlaceholder: 'アプリパスワード',
      rememberMe: 'ログイン状態を保持',
      loginButton: 'ログイン',
      loggingIn: 'ログイン中...',
      createAppPassword: 'アプリパスワードを作成',
      appPasswordHint: 'セキュリティのため、アプリパスワードをご使用ください',
    },
    createPost: {
      title: '投稿を作成',
      placeholder: '政府のニュースや見解を共有...',
      findSources: 'GovGlance.orgで政府ニュースを探す',
      sources: 'ソース',
      sourcesHint: 'または govglance.org',
      addSource: 'ソースを追加',
      sourcePlaceholder: 'https://...',
      post: '投稿',
      posting: '投稿中...',
      errorNoSource: '少なくとも1つのソースを含めてください',
      errorInvalidSource: '有効なソースを入力してください',
    },
    profile: {
      followers: 'フォロワー',
      following: 'フォロー中',
      posts: '投稿',
      follow: 'フォロー',
      unfollow: 'フォロー解除',
      editProfile: 'プロフィール編集',
      myPosts: '自分の投稿',
    },
    countryPicker: {
      selectCountry: '国を選択',
      language: '言語',
      changeLanguage: '言語を変更',
    },
    censoredContent: {
      warning: 'センシティブな言葉を含む可能性があります',
      show: '表示',
      hide: '非表示',
    },
    errors: {
      generic: 'エラーが発生しました',
      networkError: 'ネットワークエラー。接続を確認してください。',
      loginFailed: 'ログインに失敗しました',
      postFailed: '投稿に失敗しました',
      loadFailed: '読み込みに失敗しました',
    },
    common: {
      cancel: 'キャンセル',
      save: '保存',
      delete: '削除',
      edit: '編集',
      close: '閉じる',
      back: '戻る',
      next: '次へ',
      done: '完了',
      retry: '再試行',
      loading: '読み込み中...',
    },
  },

  es: {
    appName: 'GovSky',
    onboarding: {
      welcome: 'Bienvenido a GovSky',
      subtitle: 'Tu portal a noticias gubernamentales en Bluesky',
      selectCountry: 'Selecciona tu país',
      selectLanguage: 'Elige tu idioma',
      continueButton: 'Continuar',
      ageConfirm: 'Al continuar, confirmo que tengo 16 años o más.',
      privacyLink: 'Política de privacidad',
      poweredBy: 'por',
    },
    header: {
      login: 'Iniciar sesión',
      logout: 'Cerrar sesión',
      profile: 'Perfil',
    },
    tabs: {
      recent: 'Recientes',
      trending: 'Tendencias',
      officials: 'Oficiales',
      saved: 'Guardados',
    },
    feed: {
      loading: 'Cargando...',
      loadMore: 'Cargar más',
      noPostsYet: 'Aún no hay publicaciones',
      noSavedPosts: 'No hay publicaciones guardadas',
      savePostsHint: 'Guarda publicaciones para verlas aquí',
      errorLoading: 'Error al cargar publicaciones',
      pullToRefresh: 'Desliza para actualizar',
    },
    post: {
      like: 'Me gusta',
      liked: 'Te gusta',
      repost: 'Republicar',
      reposted: 'Republicado',
      reply: 'Responder',
      share: 'Compartir',
      bookmark: 'Guardar',
      bookmarked: 'Guardado',
      viewPost: 'Ver publicación',
      copiedToClipboard: 'Copiado al portapapeles',
      showMore: 'Ver más',
      showLess: 'Ver menos',
      repostedBy: 'Republicado por',
      quotedPost: 'Publicación citada',
    },
    login: {
      title: 'Iniciar sesión en Bluesky',
      description: 'Usa tu identificador de Bluesky y contraseña de aplicación',
      handlePlaceholder: 'Identificador (ej: usuario.bsky.social)',
      passwordPlaceholder: 'Contraseña de aplicación',
      rememberMe: 'Recordarme',
      loginButton: 'Iniciar sesión',
      loggingIn: 'Iniciando sesión...',
      createAppPassword: 'Crear contraseña de aplicación',
      appPasswordHint: 'Por seguridad, usa una contraseña de aplicación',
    },
    createPost: {
      title: 'Crear publicación',
      placeholder: 'Comparte noticias gubernamentales...',
      findSources: 'Encuentra noticias en GovGlance.org',
      sources: 'Fuentes',
      sourcesHint: 'o govglance.org',
      addSource: 'Añadir fuente',
      sourcePlaceholder: 'https://...',
      post: 'Publicar',
      posting: 'Publicando...',
      errorNoSource: 'Incluye al menos una fuente',
      errorInvalidSource: 'Proporciona fuentes válidas',
    },
    profile: {
      followers: 'Seguidores',
      following: 'Siguiendo',
      posts: 'Publicaciones',
      follow: 'Seguir',
      unfollow: 'Dejar de seguir',
      editProfile: 'Editar perfil',
      myPosts: 'Mis Publicaciones',
    },
    countryPicker: {
      selectCountry: 'Seleccionar país',
      language: 'Idioma',
      changeLanguage: 'Cambiar idioma',
    },
    censoredContent: {
      warning: 'El contenido puede contener lenguaje sensible',
      show: 'Mostrar',
      hide: 'Ocultar',
    },
    errors: {
      generic: 'Algo salió mal',
      networkError: 'Error de red. Verifica tu conexión.',
      loginFailed: 'Error al iniciar sesión',
      postFailed: 'Error al crear publicación',
      loadFailed: 'Error al cargar contenido',
    },
    common: {
      cancel: 'Cancelar',
      save: 'Guardar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Cerrar',
      back: 'Atrás',
      next: 'Siguiente',
      done: 'Listo',
      retry: 'Reintentar',
      loading: 'Cargando...',
    },
  },
};

// Get default language based on country
export const getDefaultLanguage = (countryCode) => {
  const countryLanguageMap = {
    AU: 'en',
    BR: 'pt',
    CA: 'en', // Default to English, user can choose French
    CH: 'zh',
    GE: 'de',
    JP: 'ja',
    SI: 'en',
    SP: 'es',
    UK: 'en',
    US: 'en',
  };
  return countryLanguageMap[countryCode] || 'en';
};

// Get translation helper
export const getTranslation = (language, key) => {
  const keys = key.split('.');
  let value = translations[language] || translations.en;
  
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) {
      // Fallback to English
      value = translations.en;
      for (const fallbackKey of keys) {
        value = value?.[fallbackKey];
      }
      break;
    }
  }
  
  return value || key;
};

